import { memo, useEffect, useSyncExternalStore, type ReactElement } from "react";
import { COIN_ICON_KEYS, HUD, coinParts, type Rules } from "odyssey-shared";
import { Toast, logical } from "../../ui/kit/index.ts";
import type { DiscoveryShown } from "./discovery.ts";
import { itemGlyph } from "../bag/describe.ts";
import { iconArt } from "../icons/art.ts";
import { coinGainWords } from "../wallet/describe.ts";
import { itemArt, useIcons } from "../icons/context.tsx";
import { lostLine, toastTitle } from "../chest/describe.ts";
import type { LootToast } from "../chest/toasts.ts";
import { levelUpTitle, type LevelUpShown } from "./level-up.ts";
import type { WarningSnapshot } from "./maintenance-warning.ts";
import { slotRect } from "./area-slots.ts";
import type { HudArea } from "./slots.ts";
import { say } from "../../words.ts";

/**
 * The toast column in `HUD_SLOTS.toasts` (the design's `Hud.jsx` toasts slot: a column bottom-aligned with a 2-pixel
 * gap, newest at the bottom): the maintenance warning's toast (ADR-078 decision 6) above the level-up toast (P2.4-T4),
 * which slides in on the motion table's `toast-in` and leaves with a single blink (ADR-061's note of 2026-10-02), with
 * neither under reduced motion (`hud.css`), above the discovery toasts (P2.11-T8, the design's `Discovery` notice),
 * oldest first, which enter and leave the same way, above the loot toasts (P2.8-T16, ADR-091 decision 19; the design's
 * `loot-notices` card, its `items` state): one for each item a take or a creature's drop moved to the bag, its colour
 * art untinted (ADR-111 decision 15) or its glyph, and its name with the count above one, with no line saying where it
 * came from (the author's ruling of 2026-10-09), the copper a creature's drop paid (its `gold` state, P2.8-T39), the art
 * of the coin of its largest part, as the wallet draws it, or the coin glyph before the atlas decodes, and the copper's gain in full words ("+7 Bits", ADR-124's note for the coin toast's words) in the positive colour, with no second
 * line either, and the notice of lines a full bag could not take (its `full` state, P2.8-T62), "Your bags are full."
 * over the lines, entering and leaving as the level-up toast does. The slot is drawn only while
 * a toast shows, and nothing in it takes the pointer; it draws only when a toast or the layout changes.
 */

/**
 * What the column reads a toast from: a snapshot and a way to hear that it changed, both working unbound.
 *
 * @typeParam T The snapshot.
 */
export interface ToastSource<T> {
  readonly subscribe: (listener: () => void) => () => void;
  readonly getSnapshot: () => T;
}

/**
 * A source that never shows a toast, for a page without one of the two.
 */
const NONE: ToastSource<null> = { subscribe: () => () => undefined, getSnapshot: () => null };

/**
 * The discovery toasts of a page without them: one empty list, so its snapshot never changes.
 */
const NO_DISCOVERIES: readonly DiscoveryShown[] = [];

/**
 * A source that never shows a discovery toast.
 */
const UNDISCOVERED: ToastSource<readonly DiscoveryShown[]> = { subscribe: () => () => undefined, getSnapshot: () => NO_DISCOVERIES };

/**
 * No loot toasts, the same list each time it is read.
 */
const NO_LOOT: readonly LootToast[] = [];

/**
 * A source that never shows a loot toast, for a page without them.
 */
const NO_LOOT_SOURCE: ToastSource<readonly LootToast[]> = { subscribe: () => () => undefined, getSnapshot: () => NO_LOOT };

/**
 * The loot toasts a page hands the column: their `source` and the `rules` their items are named and drawn by.
 */
export interface HudLoot {
  readonly source: ToastSource<readonly LootToast[]>;
  readonly rules: Rules;
}

/**
 * The column.
 *
 * @param props The `area` it is laid out on, the page's maintenance `warnings`, its `levelUp` toast, its `discoveries` and its
 *   `loot` toasts, each absent on a page without it, and `onCommit`, told after each commit.
 * @returns The slot with its toasts, or nothing while none shows.
 */
export const ToastColumn = memo(function ToastColumn({ area, warnings, levelUp, discoveries, loot, onCommit }: { readonly area: HudArea; readonly warnings: ToastSource<WarningSnapshot> | undefined; readonly levelUp: ToastSource<LevelUpShown | null> | undefined; readonly discoveries?: ToastSource<readonly DiscoveryShown[]> | undefined; readonly loot?: HudLoot | undefined; readonly onCommit: (() => void) | undefined }): ReactElement | null {
  const warning = useSyncExternalStore((warnings ?? NONE).subscribe, (warnings ?? NONE).getSnapshot, (warnings ?? NONE).getSnapshot);
  const level = useSyncExternalStore((levelUp ?? NONE).subscribe, (levelUp ?? NONE).getSnapshot, (levelUp ?? NONE).getSnapshot);
  const found = useSyncExternalStore((discoveries ?? UNDISCOVERED).subscribe, (discoveries ?? UNDISCOVERED).getSnapshot, (discoveries ?? UNDISCOVERED).getSnapshot);
  const gains = useSyncExternalStore((loot?.source ?? NO_LOOT_SOURCE).subscribe, (loot?.source ?? NO_LOOT_SOURCE).getSnapshot, (loot?.source ?? NO_LOOT_SOURCE).getSnapshot);
  const icons = useIcons();
  useEffect(() => {
    onCommit?.();
  });
  const toast = warning === null ? null : warning.toast;
  if (toast === null && level === null && found.length === 0 && gains.length === 0) {
    return null;
  }
  const rect = slotRect("toasts", area);
  return (
    <div className="ot-hud-slot ot-hud-toasts" data-hud-slot="toasts" style={{ left: logical(rect.left), top: logical(rect.top), width: logical(rect.width), height: logical(rect.height), rowGap: logical(HUD.gutter) }}>
      {toast === null ? null : <Toast tone="soon" title={toast.title} text={toast.text} />}
      {level === null ? null : (
        <div key={level.level} className={`ot-hud-toast${level.leaving ? " is-leaving" : ""}`} data-hud="level-up" data-level={level.level} data-leaving={level.leaving}>
          <Toast tone="gain" title={levelUpTitle(level.level)} />
        </div>
      )}
      {found.map((discovery) => (
        <div key={`discovery-${discovery.id}`} className={`ot-hud-toast${discovery.leaving ? " is-leaving" : ""}`} data-hud="discovery" data-leaving={discovery.leaving}>
          <Toast tone="discovery" title={discovery.title} text={discovery.text} gain={discovery.gain ?? undefined} />
        </div>
      ))}
      {loot === undefined
        ? null
        : gains.map((gain) =>
            gain.from === "full" ? (
              <div key={`loot-${gain.id}`} className={`ot-hud-toast${gain.leaving ? " is-leaving" : ""}`} data-hud="loot-full" data-leaving={gain.leaving}>
                <Toast tone="full" title={say("chest.lines.full")} text={lostLine(loot.rules, gain.lost)} />
              </div>
            ) : gain.from === "coin" ? (
              <div key={`loot-${gain.id}`} className={`ot-hud-toast${gain.leaving ? " is-leaving" : ""}`} data-hud="loot-coin" data-copper={gain.quantity} data-leaving={gain.leaving}>
                <Toast tone="gain" glyph="coin" art={iconArt(icons.snapshot, COIN_ICON_KEYS[coinParts(gain.quantity)[0]?.coin ?? "copper"], "coin toast", 16, icons.log)} title={coinGainWords(gain.quantity)} />
              </div>
            ) : (
              <div key={`loot-${gain.id}`} className={`ot-hud-toast${gain.leaving ? " is-leaving" : ""}`} data-hud="loot-toast" data-item={gain.item} data-quantity={gain.quantity} data-from={gain.from} data-leaving={gain.leaving}>
                <Toast tone="gain" glyph={itemGlyph(loot.rules, gain.item) ?? "briefcase-small"} art={itemArt(icons, loot.rules, gain.item, 16)} title={toastTitle(loot.rules, gain.item, gain.quantity)} />
              </div>
            ),
          )}
    </div>
  );
});
